import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Plus, Trash2, Pencil, Loader2, GripVertical } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Button } from '@foundation/components/ui/button';
import { useToast } from '@foundation/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@foundation/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@foundation/components/ui/alert-dialog';
import RevisrLayout from '../../components/RevisrLayout';
import { Label } from '@foundation/components/ui/label';
import type { ContextCategory, ContextQuestion } from '../../services/contextCategoryService';
import {
  fetchContextCategoriesAdmin,
  createQuestion,
  updateQuestion,
  deleteQuestion,
} from '../../services/contextCategoryService';
import type { QuestionFormData } from './components/QuestionForm';
import { QuestionForm } from './components/QuestionForm';

const INPUT_LABELS: Record<string, string> = {
  'yes-no': 'Yes / No',
  'yes-no-not-sure': 'Yes / No / Not Sure',
  'single-select': 'Single Select',
  'multi-select': 'Multi Select',
  'text': 'Text',
  'textarea': 'Textarea',
};

const INPUT_CLASS =
  'w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#33b864]/30 focus:border-[#33b864]/40 transition-all duration-200';

export const ContextCategoryEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();

  const [category, setCategory] = useState<ContextCategory | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Question dialog
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<ContextQuestion | null>(null);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'question'; id: string; name: string } | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const cats = await fetchContextCategoriesAdmin();
      const found = cats.find((c) => c.id === id) ?? null;
      setCategory(found);
    } catch {
      toast({ title: 'Error', description: 'Failed to load category', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  // ── Questions ──────────────────────────────────────────────────────────────

  const openNewQuestion = () => {
    setEditingQuestion(null);
    setQuestionDialogOpen(true);
  };

  const openEditQuestion = (q: ContextQuestion) => {
    setEditingQuestion(q);
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = async (data: QuestionFormData) => {
    if (!id) return;
    if (editingQuestion) {
      await updateQuestion(id, editingQuestion.id, {
        text: data.text,
        input_type: data.input_type,
        options: data.options.length > 0 ? data.options : undefined,
        placeholder: data.placeholder || undefined,
        used_for: data.used_for || undefined,
        is_required: data.is_required,
      });
    } else {
      await createQuestion(id, {
        text: data.text,
        input_type: data.input_type,
        options: data.options.length > 0 ? data.options : undefined,
        placeholder: data.placeholder || undefined,
        used_for: data.used_for || undefined,
        is_required: data.is_required,
        order_index: category?.questions.length ?? 0,
      });
    }
    await load();
    setQuestionDialogOpen(false);
    toast({ title: 'Saved', description: `Question ${editingQuestion ? 'updated' : 'added'}.` });
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget || !id) return;
    try {
      await deleteQuestion(id, deleteTarget.id);
      await load();
      toast({ title: 'Deleted', description: 'Question deleted.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeleteTarget(null);
    }
  };

  if (isLoading) {
    return (
      <RevisrLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-6 w-6 animate-spin text-[#33b864]" />
        </div>
      </RevisrLayout>
    );
  }

  if (!category) {
    return (
      <RevisrLayout>
        <div className="p-8 text-center text-muted-foreground">Category not found.</div>
      </RevisrLayout>
    );
  }

  return (
    <RevisrLayout>
      <div className="p-8 max-w-3xl">
        {/* Back + header */}
        <div className="mb-6">
          <a
            href="/revisr/admin/context-categories"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Context Categories
          </a>
          <h1 className="text-2xl font-bold text-foreground">{category.name}</h1>
          {category.description && (
            <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
          )}
        </div>

        {/* Questions section */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Questions</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Org users must answer all required questions to unlock documents in this category.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={openNewQuestion} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Add Question
            </Button>
          </div>
          {category.questions.length === 0 ? (
            <div className="px-6 py-6 text-sm text-muted-foreground italic">
              No questions yet. Add questions to require org users to answer before unlocking documents.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {category.questions.map((q, idx) => (
                <div key={q.id} className="flex items-start gap-3 px-6 py-4 hover:bg-muted/20">
                  <GripVertical className="h-4 w-4 text-muted-foreground/40 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {idx + 1}. {q.text}
                      {q.isRequired && <span className="text-destructive ml-0.5">*</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Type: {INPUT_LABELS[q.inputType] ?? q.inputType}
                      {q.options && q.options.length > 0 && ` (${q.options.length} options)`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => openEditQuestion(q)}
                    className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors flex-shrink-0"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget({ type: 'question', id: q.id, name: q.text.slice(0, 50) })}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive transition-colors flex-shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Question dialog */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            <QuestionForm
              initial={editingQuestion ? {
                text: editingQuestion.text,
                input_type: editingQuestion.inputType,
                options: editingQuestion.options ?? [],
                placeholder: editingQuestion.placeholder ?? '',
                used_for: editingQuestion.usedFor ?? '',
                is_required: editingQuestion.isRequired,
              } : undefined}
              onSave={handleSaveQuestion}
              onCancel={() => setQuestionDialogOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete question?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RevisrLayout>
  );
};
